import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, accountApi, type AccountView } from '../api/client';
import { campaignsApi } from '../api/campaigns';
import {
  Alert,
  Card,
  CardGrid,
  HelpIcon,
  LoadingState,
  Metric,
  PageHeader,
  PageLayout,
  Section,
} from '../components';
import { useI18n } from '../i18n';
import { useSession } from '../auth/session-context';
import { formatMoney } from '../format';

/**
 * Dashboard des Kundenadmins.
 *
 * Es zeigt die sechs Kennzahlen aus 02_navigation_ux_tutorial_i18n.md. Was
 * noch nicht erhoben wird - Gesprächsminuten, Caller online, laufende
 * Gespräche, Abschlüsse -, steht bewusst als Strich mit dem Hinweis „Noch
 * keine Zahlen" da. Hier wird nichts geschätzt und nichts erfunden; die
 * Zahlen entstehen mit dem Dialer und der Statistik.
 */
export function DashboardPage(): JSX.Element {
  const { t, tError } = useI18n();
  const { principal, handleApiError } = useSession();

  const [account, setAccount] = useState<AccountView | null>(null);
  const [aktiveKampagnen, setAktiveKampagnen] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  const laden = useCallback(async () => {
    setError(null);
    try {
      const [konto, kampagnen] = await Promise.all([accountApi.get(), campaignsApi.list()]);
      setAccount(konto.account);
      setAktiveKampagnen(kampagnen.campaigns.filter((campaign) => campaign.isActive).length);
    } catch (caught) {
      handleApiError(caught);
      setError(caught instanceof ApiError ? tError(caught.code) : tError(undefined));
    } finally {
      setLaedt(false);
    }
  }, [handleApiError, tError]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const name = principal?.kind === 'tenant_user' ? principal.displayName : '';
  const keineZahl = '–';

  return (
    <PageLayout>
      <PageHeader title={t('dashboard.title')} description={t('dashboard.welcome', { name })} />

      {error ? <Alert tone="error">{error}</Alert> : null}
      {laedt ? <LoadingState /> : null}

      <CardGrid>
        <Metric
          label={t('dashboard.balance')}
          value={account ? formatMoney(account.balance) : keineZahl}
          help={<HelpIcon text={t('dashboard.help.balance')} label={t('dashboard.balance')} />}
        />
        <Metric
          label={t('dashboard.minutesToday')}
          value={keineZahl}
          hint={t('dashboard.noData')}
          help={<HelpIcon text={t('dashboard.help.minutesToday')} label={t('dashboard.minutesToday')} />}
        />
        <Metric
          label={t('dashboard.activeCallers')}
          value={keineZahl}
          hint={t('dashboard.noData')}
          help={<HelpIcon text={t('dashboard.help.activeCallers')} label={t('dashboard.activeCallers')} />}
        />
        <Metric
          label={t('dashboard.runningCalls')}
          value={keineZahl}
          hint={t('dashboard.noData')}
          help={<HelpIcon text={t('dashboard.help.runningCalls')} label={t('dashboard.runningCalls')} />}
        />
        <Metric
          label={t('dashboard.campaigns')}
          value={aktiveKampagnen === null ? keineZahl : String(aktiveKampagnen)}
          help={<HelpIcon text={t('dashboard.help.campaigns')} label={t('dashboard.campaigns')} />}
        />
        <Metric
          label={t('dashboard.resultsToday')}
          value={keineZahl}
          hint={t('dashboard.noData')}
          help={<HelpIcon text={t('dashboard.help.resultsToday')} label={t('dashboard.resultsToday')} />}
        />
      </CardGrid>

      {aktiveKampagnen === 0 ? (
        <Card>
          <Section title={t('dashboard.quickStart')} description={t('dashboard.quickStartText')}>
            <div className="row-actions">
              <Link className="btn btn-primary" to="/kampagnen">
                {t('dashboard.openCampaigns')}
              </Link>
            </div>
          </Section>
        </Card>
      ) : null}

      {account ? (
        <Card>
          <Section title={t('balance.title')}>
            <dl className="facts">
              <dt>{t('balance.current')}</dt>
              <dd>{formatMoney(account.balance)}</dd>
              <dt>{t('balance.pricePerMinute')}</dt>
              <dd>{formatMoney(account.pricePerMinute, 3)}</dd>
            </dl>
            <div className="row-actions">
              <Link className="btn btn-secondary" to="/guthaben">
                {t('nav.balance')}
              </Link>
            </div>
          </Section>
        </Card>
      ) : null}
    </PageLayout>
  );
}
